import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import styled from 'styled-components';
import { FaGavel, FaTimes } from 'react-icons/fa';
import { useAuctionAdmin } from '../../hooks/useAuctionAdmin';
const Overlay = styled.div `
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;
const Modal = styled.div `
  background: linear-gradient(135deg, #1a1a2e, #16213e);
  border: 1px solid rgba(230, 192, 104, 0.2);
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
`;
const ModalHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;
const ModalTitle = styled.h2 `
  color: #e6c068;
  font-size: 1.3rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;
const CloseButton = styled.button `
  background: none;
  border: none;
  color: rgba(240, 240, 240, 0.5);
  cursor: pointer;
  font-size: 1.2rem;
  padding: 4px;

  &:hover { color: #f0f0f0; }
`;
const DiamondPreview = styled.div `
  background: rgba(15, 52, 96, 0.3);
  border: 1px solid rgba(230, 192, 104, 0.1);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
`;
const DiamondTitle = styled.div `
  color: #f0f0f0;
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 4px;
`;
const DiamondMeta = styled.div `
  color: rgba(240, 240, 240, 0.5);
  font-size: 0.85rem;
`;
const FormGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;
const FormGroup = styled.div `
  grid-column: ${({ $fullWidth }) => $fullWidth ? '1 / -1' : 'auto'};
`;
const Label = styled.label `
  display: block;
  color: rgba(240, 240, 240, 0.7);
  font-size: 0.85rem;
  margin-bottom: 6px;
`;
const Input = styled.input `
  width: 100%;
  padding: 10px 14px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(230, 192, 104, 0.2);
  border-radius: 8px;
  color: #f0f0f0;
  font-size: 0.9rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #e6c068;
  }
`;
const Select = styled.select `
  width: 100%;
  padding: 10px 14px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(230, 192, 104, 0.2);
  border-radius: 8px;
  color: #f0f0f0;
  font-size: 0.9rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #e6c068;
  }

  option {
    background: #1a1a2e;
    color: #f0f0f0;
  }
`;
const SubmitButton = styled.button `
  width: 100%;
  padding: 14px;
  margin-top: 24px;
  background: linear-gradient(135deg, #e6c068, #d4a848);
  color: #1a1a2e;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 20px rgba(230, 192, 104, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;
const ErrorMessage = styled.div `
  padding: 10px 14px;
  background: rgba(255, 100, 100, 0.1);
  border: 1px solid rgba(255, 100, 100, 0.2);
  border-radius: 8px;
  color: #ff6464;
  font-size: 0.85rem;
  margin-top: 12px;
`;
const SuccessMessage = styled.div `
  padding: 16px;
  background: rgba(72, 199, 142, 0.1);
  border: 1px solid rgba(72, 199, 142, 0.2);
  border-radius: 8px;
  color: #48c78e;
  font-size: 0.95rem;
  text-align: center;
  margin-top: 16px;
`;
const formatPrice = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
const CreateAuctionModal = ({ diamond, onClose, onCreated }) => {
    const { createAuction, isCreating, createError } = useAuctionAdmin();
    const [createdAuctionId, setCreatedAuctionId] = useState(null);
    const [formData, setFormData] = useState({
        auctionType: 'automated',
        visibility: 'public',
        minimumDealerTier: 5,
        startingPrice: diamond.totalPrice ? Math.round(diamond.totalPrice * 0.8) : 0,
        minimumIncrementPercent: 2,
        reservePrice: diamond.totalPrice || 0,
        scheduledStartTime: '',
        scheduledEndTime: '',
        gracePeriodHours: 2,
        maxGraceExtensions: 5,
    });
    const handleSubmit = async (e) => {
        e.preventDefault();
        const input = {
            diamondId: diamond.id,
            diamondKey1: diamond.lab, // Key1 = lab in diamonds container
            ...formData,
        };
        const result = await createAuction(input);
        if (result.success && 'auctionId' in result && result.auctionId) {
            setCreatedAuctionId(result.auctionId);
            onCreated?.(result.auctionId);
        }
    };
    const updateField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };
    if (createdAuctionId) {
        return (_jsx(Overlay, { onClick: onClose, children: _jsxs(Modal, { onClick: (e) => e.stopPropagation(), children: [_jsxs(SuccessMessage, { children: [_jsx(FaGavel, { style: { marginRight: 8 } }), "Auction created successfully!", _jsx("br", {}), _jsxs("span", { style: { fontSize: '0.8rem', opacity: 0.7 }, children: ["ID: ", createdAuctionId] })] }), _jsx(SubmitButton, { onClick: onClose, style: { marginTop: 16 }, children: "Close" })] }) }));
    }
    return (_jsx(Overlay, { onClick: onClose, children: _jsxs(Modal, { onClick: (e) => e.stopPropagation(), children: [_jsxs(ModalHeader, { children: [_jsxs(ModalTitle, { children: [_jsx(FaGavel, {}), " Create Auction"] }), _jsx(CloseButton, { onClick: onClose, children: _jsx(FaTimes, {}) })] }), _jsxs(DiamondPreview, { children: [_jsxs(DiamondTitle, { children: [diamond.caratWeight, "ct ", diamond.color, " ", diamond.clarity, " ", diamond.shape] }), _jsxs(DiamondMeta, { children: [diamond.lab, " | Stock: ", diamond.stockNumber || 'N/A', " | Value: ", formatPrice(diamond.totalPrice || 0)] })] }), _jsxs("form", { onSubmit: handleSubmit, children: [_jsxs(FormGrid, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Auction Type" }), _jsxs(Select, { value: formData.auctionType, onChange: (e) => updateField('auctionType', e.target.value), children: [_jsx("option", { value: "automated", children: "Automated (eBay-style)" }), _jsx("option", { value: "supervised", children: "Supervised (Christie's-style)" })] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Visibility" }), _jsxs(Select, { value: formData.visibility, onChange: (e) => updateField('visibility', e.target.value), children: [_jsx("option", { value: "public", children: "Public" }), _jsx("option", { value: "private", children: "Private (Invite Only)" })] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Starting Price ($)" }), _jsx(Input, { type: "number", value: formData.startingPrice, onChange: (e) => updateField('startingPrice', parseFloat(e.target.value) || 0), required: true })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Reserve Price ($)" }), _jsx(Input, { type: "number", value: formData.reservePrice, onChange: (e) => updateField('reservePrice', parseFloat(e.target.value) || 0) })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Min Increment (%)" }), _jsx(Input, { type: "number", value: formData.minimumIncrementPercent, onChange: (e) => updateField('minimumIncrementPercent', parseFloat(e.target.value) || 2), min: 1, max: 50 })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Min Dealer Tier" }), _jsxs(Select, { value: formData.minimumDealerTier, onChange: (e) => updateField('minimumDealerTier', parseInt(e.target.value)), children: [_jsx("option", { value: 4, children: "Basic Dealer (4)" }), _jsx("option", { value: 5, children: "Verified Dealer (5)" }), _jsx("option", { value: 6, children: "Senior Dealer (6)" })] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Start Time" }), _jsx(Input, { type: "datetime-local", value: formData.scheduledStartTime, onChange: (e) => updateField('scheduledStartTime', e.target.value ? new Date(e.target.value).toISOString() : ''), required: true })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "End Time" }), _jsx(Input, { type: "datetime-local", value: formData.scheduledEndTime, onChange: (e) => updateField('scheduledEndTime', e.target.value ? new Date(e.target.value).toISOString() : ''), required: true })] }), formData.auctionType === 'automated' && (_jsxs(_Fragment, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Grace Period (hours)" }), _jsx(Input, { type: "number", value: formData.gracePeriodHours, onChange: (e) => updateField('gracePeriodHours', parseInt(e.target.value) || 2), min: 1, max: 24 })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Max Extensions" }), _jsx(Input, { type: "number", value: formData.maxGraceExtensions, onChange: (e) => updateField('maxGraceExtensions', parseInt(e.target.value) || 5), min: 1, max: 20 })] })] }))] }), createError && _jsx(ErrorMessage, { children: createError }), _jsx(SubmitButton, { type: "submit", disabled: isCreating, children: isCreating ? 'Creating Auction...' : 'Create Auction' })] })] }) }));
};
export default CreateAuctionModal;
